import styled from '@emotion/styled';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import SectionTitle from '../../SectionTitle';
import Container from '../Container';
import AccordionLayout from './AccordionLayout';
import Typography from '../../text/Typography';
import RichText from '../../../contentful-elements/RichText/RichText';
import Image from '../../Image';
import { css } from '@emotion/react';
import GhostButton from '../../buttons/GhostButton';
import type { Document } from '@contentful/rich-text-types';

const TopContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  margin-top: ${spacing[6]}px;
  margin-bottom: ${spacing[5]}px;
  padding: 0;

  ${breakpoints.desktop} {
    padding: 0 ${spacing[5]}px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 250px;

  ${breakpoints.desktop} {
    min-width: 280px;
    max-width: 320px;
    height: auto;
    flex: 1 0 0;
    align-self: stretch;
  }
`;

const ImageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PillarDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[5]}px;

  ${breakpoints.desktop} {
    flex-direction: row;
    gap: ${spacing[6]}px;
  }
`;

const LogoImage = styled(Image)`
  height: ${spacing[5]}px;
  width: auto;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(7%) sepia(0%) saturate(542%) hue-rotate(148deg)
    brightness(98%) contrast(101%);
  opacity: 0.7;
  ${breakpoints.tablet} {
    height: ${spacing[7]}px;
  }
`;

const ApproachesWrapper = styled.div`
  display: flex;
  gap: ${spacing[5]}px;
  flex-wrap: wrap;
`;

const ApproachContainer = styled.div`
  display: flex;
  gap: ${spacing[3]}px;
  align-items: center;
  width: 100%;

  ${breakpoints.tablet} {
    width: 45%;
  }
`;

export type FundPillarAccordionData = {
  title?: string;
  description?: string;
  selectionCriteria?: Document;
  image?: {
    url: string;
    alt: string;
  };
  approaches: Array<{
    id: string;
    name: string;
    icon?: {
      url: string;
      alt: string;
    };
  }>;
  randomizedInitiative?: {
    description?: string;
    slug?: string;
    title?: string;
    logo?: {
      url: string;
      alt: string;
      fileName?: string;
    };
  };
};

export default function FundPillars({
  sectionTitle,
  fundPillarsWithData,
  fundPillarsTitle,
  fundPillarsParagraph,
  fundSlug,
}: {
  sectionTitle?: string;
  fundPillarsWithData?: FundPillarAccordionData[];
  fundPillarsTitle?: string;
  fundPillarsParagraph?: string;
  fundSlug?: string;
}) {
  if (!sectionTitle && !fundPillarsWithData?.length && !fundPillarsTitle && !fundPillarsParagraph)
    return null;

  const items = fundPillarsWithData?.map((fundPillar) => ({
    title: fundPillar.title,
    expandedContent: (
      <div css={{ display: 'flex', flexDirection: 'column', gap: `${spacing[8]}px` }}>
        <PillarDescription>
          {fundPillar.image && (
            <ImageWrapper>
              <Image
                maxWidth={{ desktop: 500 }}
                lazyLoad
                url={fundPillar.image.url}
                alt={fundPillar.image.alt}
                css={ImageStyle}
              />
            </ImageWrapper>
          )}
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 0 0',
              gap: `${spacing[6]}px`,
            }}
          >
            <Typography variant="body">{fundPillar.description}</Typography>

            {!!fundPillar.approaches.length && (
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: `${spacing[4]}px`,
                }}
              >
                <Typography variant="overline">Approaches</Typography>
                <ApproachesWrapper>
                  {fundPillar.approaches.map((approach) => (
                    <ApproachContainer key={approach.id}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={approach.icon?.url} alt={approach.icon?.alt ?? ''} />
                      <Typography variant="overline">{approach.name}</Typography>
                    </ApproachContainer>
                  ))}
                </ApproachesWrapper>
              </div>
            )}
          </div>
        </PillarDescription>

        {fundPillar.selectionCriteria && (
          <div>
            <Typography variant="overline">Example selection criteria</Typography>
            <RichText document={fundPillar.selectionCriteria} withCheckmark />
          </div>
        )}

        {!!fundPillar.randomizedInitiative?.description && (
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: `${spacing[4]}px`,
            }}
          >
            <Typography variant="overline">Example project</Typography>

            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: `${spacing[3]}px`,
              }}
            >
              {fundPillar.randomizedInitiative.logo && (
                <div>
                  <LogoImage
                    url={fundPillar.randomizedInitiative.logo.url}
                    alt={fundPillar.randomizedInitiative.logo.alt}
                    fileName={fundPillar.randomizedInitiative.logo.fileName}
                  />
                </div>
              )}

              <Typography variant="body">{fundPillar.randomizedInitiative.description}</Typography>
            </div>

            {fundPillar.randomizedInitiative.slug && (
              <GhostButton
                label={fundPillar.randomizedInitiative.title}
                trailingIconName="arrow-right"
                color={colors.purple400}
                hideUnderline
                href={`/impact-funds/${fundSlug}/projects/${fundPillar.randomizedInitiative.slug}`}
              />
            )}
          </div>
        )}
      </div>
    ),
  }));

  return (
    <Container>
      {sectionTitle && <SectionTitle title={sectionTitle} />}

      <TopContentWrapper>
        {fundPillarsTitle && <Typography variant="h3">{fundPillarsTitle}</Typography>}
        {fundPillarsParagraph && <Typography variant="body">{fundPillarsParagraph}</Typography>}
      </TopContentWrapper>

      <AccordionLayout items={items} />
    </Container>
  );
}
